import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, Autocomplete, CircularProgress, Button, Typography } from '@mui/material';
import axios from './../axios/api';
import CloseIcon from '@mui/icons-material/Close';

const TagInput = ({
    label,
    placeholder,
    fetchTagsUrl,
    addTagUrl,
    tagKey = 'name',
    idKey = 'id',
    minInputLength = 2,
    renderTag,
    tags,
    setTags,
    errors, setErrors,
    helperText, error,
    ...props
}) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (errors.tags) {
            setErrors((prevErrors) => ({ ...prevErrors, tags: '' }));
        }
        if (inputValue.length >= minInputLength) {
            setLoading(true);
            axios.get(`${fetchTagsUrl}?search=${inputValue}`)
                .then(response => {
                    setSuggestions(response.data?.data?.data || []);
                    setLoading(false);
                })
                .catch(error => {
                    console.error("Error fetching suggestions:", error);
                    setLoading(false);
                });
        }
    }, [inputValue, fetchTagsUrl, minInputLength]);

    const handleAddTag = async (event, newTags) => {
        setSuggestions([]);

        const { type, key } = event;
        if (type === 'keydown' && key === 'Backspace' && inputValue === '') {
            setTags(prevTags => prevTags.slice(0, -1));
            return;
        }

        const newTagTitle = newTags[newTags.length - 1];
        const existingTagIndex = tags.findIndex(tag => tag[tagKey] === newTagTitle);

        if (existingTagIndex !== -1) {
            // Remove the tag if it already exists
            setTags(prevTags => prevTags.filter((_, index) => index !== existingTagIndex));
        } else {
            const tagExistsInSuggestions = suggestions.some(option => option[tagKey] === newTagTitle);
            if (!tagExistsInSuggestions && newTagTitle) {

                try {
                    setLoading(true);
                    const response = await axios.post(addTagUrl, { title: newTagTitle });
                    const newTag = { [idKey]: response.data?.data?.id, [tagKey]: response.data?.data?.title };
                    setTags(prevTags => [...prevTags, newTag]);
                } catch (error) {

                    console.error("Error adding tag to server:", error);
                } finally {
                    setLoading(false);
                }


            } else if (tagExistsInSuggestions) {
                const existingTag = suggestions.find(option => option[tagKey] === newTagTitle);
                if (existingTag) {
                    setTags(prevTags => [...prevTags, existingTag]);

                }
            }
        }
    };

    const onDeleteTag = (tagIndex) => {
        setTags(prevTags => prevTags.filter((_, idx) => idx !== tagIndex));
    };

    return (
        <Autocomplete
            multiple
            freeSolo
            options={suggestions.map(option => option[tagKey])}
            value={tags.map(tag => tag[tagKey])}
            onChange={handleAddTag}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Button
                        key={index}
                        variant='outlined'
                        onClick={() => onDeleteTag(index)} // Handle onDeleteTag on button click
                        sx={{ marginLeft: 0.5 }}
                    >
                        <Typography variant='button'>{option}</Typography>
                        <CloseIcon sx={{ width: 10, height: 10, marginLeft: 1 }} color="black" fontSize="small" />
                    </Button>
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={label}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    error={error}
                    helperText={helperText} // Show IDs in helper text
                />
            )}
            ListboxProps={{ style: { direction: 'rtl', textAlign: 'right' } }} // Ensures the list is RTL

            {...props}
        />
    );
};

TagInput.propTypes = {
    label: PropTypes.string,
    placeholder: PropTypes.string,
    fetchTagsUrl: PropTypes.string.isRequired,
    addTagUrl: PropTypes.string.isRequired,
    tagKey: PropTypes.string,
    idKey: PropTypes.string,
    minInputLength: PropTypes.number,
    renderTag: PropTypes.func,
};

export default TagInput;
